import React from 'react';
import PropTypes from 'prop-types';

const ChatHistory = ({ 
  sessions, 
  currentSessionId, 
  onSessionSelect, 
  onNewChat,
  onReturnToActive,
  hasActiveConversation,
  hasNewActiveMessages,
  onRetry,
  isLoading,
  error 
}) => {
  // Format date to readable string
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Get the start of today (midnight) in local timezone
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Get the start of the message date (midnight) in local timezone
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    // Calculate the difference in days
    const diffTime = today - messageDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Truncate preview text
  const truncateText = (text, maxLength = 60) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="chat-history">
      {/* Header with New Chat Button */}
      <div className="chat-history-header">
        <button 
          className="new-chat-button"
          onClick={onNewChat}
          aria-label="Start new chat"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>New Chat</span>
        </button>
      </div>

      {/* Return to Active Chat Notice */}
      {hasActiveConversation && onReturnToActive && (
        <div className={`return-to-active-notice ${hasNewActiveMessages ? 'has-new-messages' : ''}`}>
          <div className="active-indicator">
            <span className="pulse-dot"></span>
            <span className="active-text">
              Active conversation
              {hasNewActiveMessages && <span className="new-messages-badge">New</span>}
            </span>
          </div>
          <button 
            className="return-to-active-button"
            onClick={onReturnToActive}
            aria-label="Return to active conversation"
            title={hasNewActiveMessages ? "New messages received!" : "Return to active conversation"}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Return to chat</span>
            {hasNewActiveMessages && (
              <span className="notification-dot"></span>
            )}
          </button>
        </div>
      )}

      {/* Sessions List */}
      <div className="chat-history-list">
        {isLoading && (
          <div className="chat-history-loading">
            <div className="loading-spinner-small"></div>
            <p>Loading history...</p>
          </div>
        )}

        {error && (
          <div className="chat-history-error">
            <p>Failed to load chat history</p>
            {onRetry && (
              <button className="retry-button" onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        )}

        {!isLoading && !error && sessions.length === 0 && (
          <div className="chat-history-empty">
            <p>No chat history yet</p>
            <p className="empty-subtitle">Start a conversation!</p>
          </div>
        )}

        {!isLoading && !error && sessions.length > 0 && (
          <>
            {sessions.map((session) => {
              const isActive = session.session_id === currentSessionId;
              
              return (
                <div
                  key={session.session_id}
                  className={`chat-history-item ${isActive ? 'active' : ''}`}
                  onClick={() => onSessionSelect(session)}
                >
                  <div className="session-header">
                    <span className="session-title">
                      {session.conversation_title || 'Untitled Chat'}
                    </span>
                    <span className="session-date">
                      {formatDate(session.last_modified)}
                    </span>
                  </div>
                  <div className="session-details">
                    <span className="session-time">
                      {formatTime(session.last_modified)}
                    </span>
                    <span className="session-count">
                      {session.message_count} {session.message_count === 1 ? 'message' : 'messages'}
                    </span>
                  </div>
                  <div className="session-preview">
                    {truncateText(session.last_message_preview)}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

ChatHistory.propTypes = {
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      session_id: PropTypes.string.isRequired,
      conversation_title: PropTypes.string,
      message_count: PropTypes.number,
      last_modified: PropTypes.string,
      last_message_preview: PropTypes.string,
    })
  ),
  currentSessionId: PropTypes.string,
  onSessionSelect: PropTypes.func.isRequired,
  onNewChat: PropTypes.func.isRequired,
  onReturnToActive: PropTypes.func,
  hasActiveConversation: PropTypes.bool,
  hasNewActiveMessages: PropTypes.bool,
  onRetry: PropTypes.func,
  isLoading: PropTypes.bool,
  error: PropTypes.bool,
};

ChatHistory.defaultProps = {
  sessions: [],
  currentSessionId: null,
  onReturnToActive: null,
  hasActiveConversation: false,
  hasNewActiveMessages: false,
  onRetry: null,
  isLoading: false,
  error: false,
};

export default ChatHistory;

